import { useState, useEffect, useRef } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";

import {
	API_URL,
	getValFromLS,
	setValToLS,
	fmtCurrency,
	scroll,
} from "../../helpers";

import Alert from "../../components/Alert";
import InputStripe from "../../components/InputStripe";
import Msg from "../../components/Msg";
import Submit from "../../components/Submit";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faChevronCircleRight,
	faPencil,
} from "@fortawesome/pro-light-svg-icons";

import S from "./styled";

const Review = ({ handleStatus }) => {
	const [order, setOrder] = useState({});
	const [names, setNames] = useState({});
	const [errors, setErrors] = useState([]);
	const [msg, setMsg] = useState({});
	const [alert, setAlert] = useState({
		type: "working",
		text: "",
	});

	const formElement = useRef(null);
	const scriptLoadedRef = useRef(false);

	const stripe = useStripe();
	const elements = useElements();
	const initRef = useRef(false);

	useEffect(() => {
		if (initRef.current) return;
		initRef.current = true;

		scroll();

		if (getValFromLS("order", true)) {
			setOrder(JSON.parse(getValFromLS("order", true)));
			setAlert({});
		} else {
			setAlert({
				type: "error",
				text: "No donation order found.",
			});
		}
	});

	useEffect(() => {
		// Avoid adding multiple times if user navigates back/forward
		if (scriptLoadedRef.current) return;

		const existing =
			document.querySelector(
				`script[src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"]`,
			) || null;

		if (existing) {
			scriptLoadedRef.current = true;
			return;
		}

		const script = document.createElement("script");
		script.src =
			"https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
		script.defer = true;
		script.onload = () => {
			scriptLoadedRef.current = true;
			const widgetId = window.turnstile.render("#turnstile-container", {
				sitekey: "0x4AAAAAACNujNgBwI3EoitN",
				callback: function (token) {
					handleUpdate("turnstileWidgetId", widgetId);
					// token get added to form data with name - cf-turnstile-response
				},
			});
		};

		document.head.appendChild(script);

		// optional cleanup: usually you can leave it in place
		return () => {
			// if you truly want it per‑page only, uncomment:
			// script.remove();
		};
	}, []);

	const handleUpdate = (name, value) => {
		setNames((names) => ({ ...names, [name]: value ? value : "" }));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setErrors([]);
		setMsg({
			type: "working",
			text: "",
		});

		const url = new URL(`${API_URL}/?f=donateReview`);
		let formData = new FormData(formElement.current);
		formData.append("order", JSON.stringify(order));

		try {
			const response = await fetch(url, {
				method: "POST",
				body: formData,
			});
			const json = await response.json();
			if (json && json.resp === 1) {
				handlePayment(json.total);
			} else {
				setErrors(json.fields);
				setMsg({
					type: "error",
					text: json.text,
				});
			}
		} catch (error) {
			setMsg({
				type: "error",
				text: "An error has occurred.",
			});
		}
	};

	const handlePayment = async (total) => {
		try {
			const result = await stripe.createPaymentMethod({
				type: "card",
				card: elements.getElement(CardElement),
				billing_details: {
					name: `${order.donor.fname} ${order.donor.lname}`,
					email: `${order.donor.email}`,
				},
			});

			if (result.error) {
				setMsg({
					type: "error",
					text: result.error.message,
				});
			} else {
				handleCreated(result.paymentMethod, total);
			}
		} catch (error) {
			setMsg({
				type: "error",
				text: "Please enter a payment method.",
			});
			setErrors(["card"]);
		}
	};

	const handleCreated = async (paymentMethod, total) => {
		const url = new URL(`${API_URL}/?f=donateSubmit`);
		let formData = new FormData(formElement.current);
		formData.append("order", JSON.stringify(order));
		formData.append("total", total);
		formData.append("stripePaymentMethod", JSON.stringify(paymentMethod));

		try {
			const response = await fetch(url, {
				method: "POST",
				body: formData,
			});
			const json = await response.json();
			if (json && json.resp === 1) {
				//setValToLS("order", null, true);
				setValToLS("receipt", json.id, true);
				handleStatus(1);
			} else if (json && json.resp === 2) {
				// turnstile error
				setErrors(json.fields);
				setMsg({
					type: "error",
					text: json.text,
				});
			} else {
				setErrors(json.fields);
				setMsg({
					type: "error",
					text: json.text,
				});
			}
		} catch (error) {
			setMsg({
				type: "error",
				text: "An error has occurred.",
			});
		}
	};

	const handleTurnstileReset = () => {
		window.turnstile.reset(names.turnstileWidgetId);
	};

	return (
		<>
			{alert.type ? (
				<Alert data={alert} />
			) : (
				<>
					<S.Grid>
						<S.LeftContainer></S.LeftContainer>

						<S.Container>
							<S.Table1>
								<thead>
									<tr>
										<th className="left">Item</th>
										<th>Amount</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td>
											<div>
												<div>
													<h4>
														Donation to Leaders In Furthering Education, Inc.
														(LIFE)
													</h4>

													<ul>
														<li>{`${order.donor.fname} ${order.donor.lname}`}</li>
														<li>{`${order.donor.email} / ${order.donor.phone}`}</li>
														<li>{`${order.mailing.address}, ${order.mailing.city}, ${order.mailing.st} ${order.mailing.zip}`}</li>
													</ul>
												</div>

												<button type="button" onClick={() => handleStatus(3)}>
													<FontAwesomeIcon
														icon={faPencil}
														size="lg"
														style={{ fontSize: "16px" }}
													/>
												</button>
											</div>
										</td>
										<td className="center">
											<span style={{ fontSize: "16px" }}>
												{fmtCurrency(order.amount / 100, "en-US", "USD")}
											</span>
										</td>
									</tr>
								</tbody>
							</S.Table1>

							<S.Form
								method="post"
								action="/"
								onSubmit={(e) => handleSubmit(e)}
								ref={formElement}
							>
								<S.Hdg>
									<h4>
										Please enter your payment information below to submit your
										donation.
									</h4>
								</S.Hdg>

								<S.FormRow1>
									<InputStripe
										name="card"
										label="Credit or Debit Card"
										reqd={true}
										errors={errors}
									/>
								</S.FormRow1>

								<S.Note>
									<p>Donations are tax deductible as permitted by law.</p>

									<p>
										A copy of the official registration and financial
										information may be obtained from the Division of Consumer
										Services by calling toll-free, within the state,
										1-800-435-7352. Registration does not imply endorsement,
										approval or recommendation by the state.
									</p>

									<p>
										All charges will appear as <strong>LIFE DONATION</strong> on
										your credit card statement. Your payment method will be
										charged.
									</p>

									<div
										id="turnstile-container"
										style={{ marginTop: "15px" }}
									></div>

									{errors.includes("turnstile") && (
										<p>
											<S.ResetButton
												type="button"
												onClick={() => handleTurnstileReset()}
											>
												Reset Turnstile Widget
											</S.ResetButton>
										</p>
									)}
								</S.Note>

								<S.FormMsgSubmit>
									{msg.type && <Msg data={msg} />}
									{msg.type !== "working" && (
										<Submit
											name="Submit Donation"
											icon={faChevronCircleRight}
										/>
									)}
								</S.FormMsgSubmit>
							</S.Form>

							<p style={{ marginTop: "15px", textAlign: "center" }}>
								For additional information, please call (561) 582-8083 or email:{" "}
								<a href="mailto:life@life-edu.org">life@life-edu.org</a>.
							</p>
						</S.Container>
					</S.Grid>
				</>
			)}
		</>
	);
};

export default Review;
